import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { GridExportLimit } from '@inavista/components/grid';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

// One application parameter as RIMS API returns it (api/parameters, DB-006): the company's value for
// every grid, and the grids that have their own value (key = the grid's persistenceKey).
export interface AppParameter {
  key: string;
  dataType: 'int' | 'bool' | 'string';
  value: unknown;
  overrides: Record<string, unknown>;
}

// Application parameters, loaded once at start-up. An admin will maintain them later on a parameters
// page; until then they're rows in ux.AppParameter / ux.AppParameterValue. If the call fails, every
// getter answers undefined and the screens keep today's behaviour.
@Injectable({ providedIn: 'root' })
export class AppParametersService {
  private readonly http = inject(HttpClient);
  private readonly parameters = signal<ReadonlyMap<string, AppParameter>>(new Map());

  async load(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ parameters: AppParameter[] }>(`${environment.apiUrl}parameters`, { params: { category: 'grid' } }));
      this.parameters.set(new Map(res.parameters.map(p => [p.key, p])));
    } catch (err) {
      console.error('Application parameters could not be loaded; built-in behaviour applies.', err);
    }
  }

  // The grid's own value if it has one, else the company's value.
  number(key: string, persistenceKey?: string): number | undefined {
    const p = this.parameters().get(key);
    if (!p) return undefined;
    const value = persistenceKey && persistenceKey in p.overrides ? p.overrides[persistenceKey] : p.value;
    return typeof value === 'number' ? value : undefined;
  }

  // The Export prompt's numbers for one grid (exportLimit in its GridConfig). Undefined when the
  // parameters didn't load: the grid then exports without the prompt, as before.
  gridExportLimit(persistenceKey: string): GridExportLimit | undefined {
    const max = this.number('grid.export.maxRows', persistenceKey);
    const def = this.number('grid.export.defaultRows', persistenceKey);
    if (max == null || def == null) return undefined;
    return { default: Math.min(def, max), max };
  }
}
